import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Trophy, 
  Award, 
  Sparkles, 
  X, 
  ChevronLeft, 
  ChevronRight, 
  ArrowRight, 
  Phone, 
  Target, 
  CheckCircle2, 
  Flame,
  Maximize2
} from 'lucide-react';
import { championsData } from '../data/championsData';

export default function StarPerformanceAd({ onOpenLightbox }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    // Check if user temporarily dismissed the ad in this session
    const isDismissed = sessionStorage.getItem('csa_star_ad_dismissed');
    
    if (!isDismissed) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 1200); // 1.2s smooth entrance after page load
      return () => clearTimeout(timer);
    }
  }, []);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Auto-slide effect
  useEffect(() => {
    if (!isOpen || isPaused) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % championsData.length);
    }, 4500);

    return () => clearInterval(interval);
  }, [isOpen, isPaused]);

  const handleDismiss = (dontShowToday = false) => {
    setIsOpen(false);
    setHasInteracted(true);
    if (dontShowToday) {
      localStorage.setItem('csa_star_ad_hidden_date', new Date().toDateString());
    }
    sessionStorage.setItem('csa_star_ad_dismissed', 'true');
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % championsData.length);
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + championsData.length) % championsData.length);
  };

  const currentStar = championsData[currentIndex];

  return (
    <>
      {/* --- FLOATING REOPEN BADGE (Always available when modal is closed) --- */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ scale: 0, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
            className="fixed bottom-6 left-6 z-[9990]"
          >
            <button
              onClick={() => setIsOpen(true)}
              className="group relative flex items-center gap-3 bg-zinc-950/95 hover:bg-black text-white p-2.5 pr-5 rounded-full border-2 border-gold/70 shadow-[0_0_30px_rgba(255,215,0,0.35)] backdrop-blur-xl transition-all duration-300 hover:scale-105 active:scale-95"
              aria-label="View Academy Star Performers"
            >
              {/* Pulsing glow ring */}
              <span className="absolute -top-1 -right-1 flex h-4 w-4">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-4 w-4 bg-red-600 border border-white text-[9px] font-black items-center justify-center text-white">
                  3
                </span>
              </span>

              <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-amber-600 via-gold to-yellow-200 flex items-center justify-center text-black shadow-md">
                <Trophy size={18} className="fill-black" />
              </div>
              <div className="text-left">
                <p className="text-[10px] font-black uppercase tracking-wider text-gold flex items-center gap-1">
                  <Flame size={12} className="text-red-500 fill-red-500" /> Star Champions
                </p>
                <p className="text-[11px] font-heading font-black uppercase text-white tracking-tight leading-none">
                  Proud Moments
                </p>
              </div>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* --- FULL MODAL AD SPOTLIGHT OVERLAY --- */}
      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
            {/* Backdrop with ambient blur */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => handleDismiss()}
              className="fixed inset-0 bg-black/85 backdrop-blur-xl transition-opacity"
            />

            {/* Modal Box */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 30 }}
              transition={{ type: 'spring', damping: 25, stiffness: 280 }}
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
              className="relative w-full max-w-4xl bg-gradient-to-b from-zinc-900 via-black to-zinc-950 rounded-[2.5rem] border-2 border-gold/40 shadow-[0_0_80px_rgba(255,215,0,0.25)] overflow-hidden z-10 my-auto"
            >
              {/* Top Announcement Bar */}
              <div className="bg-gradient-to-r from-red-700 via-amber-600 to-red-700 px-6 py-2.5 flex items-center justify-between text-white text-[11px] font-black uppercase tracking-[0.25em] shadow-md">
                <div className="flex items-center gap-2">
                  <Sparkles size={14} className="text-gold animate-spin" />
                  <span>Proud Moment for Chaitanya Shooting Academy</span>
                </div>
                <div className="hidden sm:flex items-center gap-4 text-[10px] tracking-widest text-zinc-200">
                  <span>★ Mission Olympic</span>
                  <span>★ Sector 81 Gurugram</span>
                </div>
                {/* Close Button */}
                <button
                  onClick={() => handleDismiss()}
                  className="bg-black/40 hover:bg-black text-white p-1.5 rounded-full transition-all hover:scale-110 ml-2"
                  aria-label="Close Announcement Ad"
                >
                  <X size={16} />
                </button>
              </div>

              {/* Modal Body: 2 Columns on md+ */}
              <div className="grid md:grid-cols-12 gap-6 p-5 sm:p-8 items-center">
                {/* LEFT: Uncropped Full Poster Display with ambient glow */}
                <div className="md:col-span-6 relative flex flex-col items-center">
                  <div className="relative w-full max-w-[340px] md:max-w-none rounded-[2rem] overflow-hidden bg-black/90 border border-white/15 p-2 shadow-2xl group">
                    {/* Background ambient poster color */}
                    <div 
                      className="absolute inset-0 opacity-30 blur-2xl transition-all duration-700"
                      style={{ backgroundColor: currentStar.medalType === 'silver' ? '#94a3b8' : currentStar.medalType === 'bronze' ? '#d97706' : '#eab308' }}
                    />

                    {/* The Full Image - No Cutoffs! */}
                    <div className="relative rounded-[1.5rem] overflow-hidden bg-zinc-950 flex items-center justify-center">
                      <img
                        src={currentStar.image}
                        alt={`${currentStar.fullName} - ${currentStar.title}`}
                        className="w-full max-h-[380px] sm:max-h-[420px] object-contain rounded-[1.3rem] transition-transform duration-500 group-hover:scale-[1.02]"
                        loading="eager"
                      />

                      {/* Medal Badge Pill */}
                      <div className="absolute top-3 left-3 bg-black/80 backdrop-blur-md px-3.5 py-1.5 rounded-full border border-white/20 flex items-center gap-2 shadow-xl">
                        <span className="text-base leading-none">{currentStar.medalIcon}</span>
                        <span className="text-[10px] font-black uppercase tracking-wider text-gold">
                          {currentStar.medal}
                        </span>
                      </div>

                      {/* Expand Button */}
                      {onOpenLightbox && (
                        <button
                          onClick={() => onOpenLightbox(currentIndex)}
                          className="absolute top-3 right-3 bg-black/80 hover:bg-red-600 text-white p-2 rounded-full border border-white/20 transition-all shadow-lg"
                          title="View Full High-Res Poster"
                        >
                          <Maximize2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Carousel Navigation Arrows & Indicators */}
                  <div className="flex items-center justify-between w-full max-w-[340px] mt-4 px-2">
                    <button
                      onClick={handlePrev}
                      className="w-9 h-9 rounded-full bg-zinc-900 border border-white/20 flex items-center justify-center text-white hover:bg-gold hover:text-black transition-all active:scale-90"
                      aria-label="Previous Champion"
                    >
                      <ChevronLeft size={18} />
                    </button>

                    <div className="flex gap-2">
                      {championsData.map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setCurrentIndex(i)}
                          className={`h-2 rounded-full transition-all duration-300 ${
                            currentIndex === i ? 'w-8 bg-gold' : 'w-2 bg-white/20 hover:bg-white/50'
                          }`}
                          aria-label={`Go to slide ${i + 1}`}
                        />
                      ))}
                    </div>

                    <button
                      onClick={handleNext}
                      className="w-9 h-9 rounded-full bg-zinc-900 border border-white/20 flex items-center justify-center text-white hover:bg-gold hover:text-black transition-all active:scale-90"
                      aria-label="Next Champion"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                </div>

                {/* RIGHT: Achievement Accolades & High Converting CTAs */}
                <div className="md:col-span-6 flex flex-col justify-between text-left space-y-4">
                  {/* Category Pill */}
                  <div className="inline-flex items-center gap-2 bg-gold/10 border border-gold/30 px-3.5 py-1.5 rounded-full w-fit">
                    <Trophy size={14} className="text-gold" />
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-gold">
                      {currentStar.event}
                    </span>
                  </div>

                  {/* Star Shooter Name & Title */}
                  <div>
                    <h3 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black uppercase italic tracking-tight text-white leading-[0.9] mb-1">
                      {currentStar.fullName}
                    </h3>
                    <p className="text-gold font-heading text-lg sm:text-xl font-bold uppercase tracking-wider">
                      {currentStar.title}
                    </p>
                  </div>

                  {/* Inspiring Quote / Description */}
                  <div className="bg-white/5 border border-white/10 p-4 rounded-2xl">
                    <p className="text-zinc-200 text-xs sm:text-sm italic leading-relaxed font-medium">
                      "{currentStar.quote}"
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-white/10">
                      {currentStar.pillars.map((pillar, i) => (
                        <span key={i} className="text-[9px] uppercase tracking-wider font-bold text-zinc-400 bg-black/60 px-2.5 py-1 rounded-md border border-white/5">
                          ✓ {pillar}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Academy Highlight Text */}
                  <div className="flex items-center gap-3 text-zinc-300 text-xs">
                    <div className="w-8 h-8 rounded-xl bg-red-600/20 text-red-500 flex items-center justify-center flex-shrink-0">
                      <Target size={16} />
                    </div>
                    <p className="text-[11px] leading-tight">
                      <strong className="text-white font-bold">Want to train like our champions?</strong> Learn with Olympic-grade electronic targets and NRAI certified coaches.
                    </p>
                  </div>

                  {/* CTA Action Buttons */}
                  <div className="space-y-2.5 pt-2">
                    <Link
                      to="/book-demo"
                      onClick={() => handleDismiss()}
                      className="w-full bg-gradient-to-r from-red-600 via-red-500 to-red-700 hover:from-white hover:to-white hover:text-black text-white py-4 px-6 rounded-2xl font-black text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-3 transition-all duration-300 shadow-[0_0_30px_rgba(220,38,38,0.4)] active:scale-95 group"
                    >
                      <Sparkles size={16} className="text-gold group-hover:text-black transition-colors" />
                      Claim Your Free Trial Session
                      <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                    </Link>

                    <div className="grid grid-cols-2 gap-2">
                      <a
                        href="#achievements"
                        onClick={() => {
                          handleDismiss();
                          const el = document.getElementById('achievements');
                          if (el) el.scrollIntoView({ behavior: 'smooth' });
                        }}
                        className="bg-zinc-900 hover:bg-zinc-800 text-white py-3 px-3 rounded-xl font-bold text-[10px] uppercase tracking-wider flex items-center justify-center gap-1.5 border border-white/10 transition-colors text-center"
                      >
                        <Award size={13} className="text-gold" />
                        Hall of Fame
                      </a>

                      <a
                        href={`tel:${currentStar.academyContact}`}
                        className="bg-zinc-900 hover:bg-zinc-800 text-white py-3 px-3 rounded-xl font-bold text-[10px] uppercase tracking-wider flex items-center justify-center gap-1.5 border border-white/10 transition-colors text-center"
                      >
                        <Phone size={13} className="text-green-400" />
                        {currentStar.academyContact}
                      </a>
                    </div>
                  </div>

                  {/* Subtext info */}
                  <div className="flex items-center justify-between text-[10px] text-zinc-500 pt-1">
                    <span>* Limited trial slots available this week</span>
                    <button 
                      onClick={() => handleDismiss(true)}
                      className="hover:text-zinc-300 underline transition-colors"
                    >
                      Don't show today
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
